import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import Loader from '../../components/Common/Loader';
import Modal from '../../components/Common/Modal';
import SegToggle from '../../components/Common/SegToggle';
import DatePicker from '../../components/Common/DatePicker';
import { CoverPattern, shade } from '../../components/Admin/CourseCover';
import { CourseIcon as LibIcon } from '../../components/Admin/iconLibrary';
import { getAccess, extendAccess, getCourses } from '../../services/adminService';
import { avatarColor, AvatarIcon } from '../../utils/avatar';

/* «Доступы» — до какого дня у ученика доступ к платформе и продление (дизайн
   Claude Design «Доступы»). Дата одна на ученика и действует на все его курсы;
   ту же дату он видит чипом «Доступ до» в профиле. Пока это подсказка: по
   истечении ничего не блокируется. */

const SOON_DAYS = 5;          // «Истекает» — осталось 5 дней и меньше, дальше «Активен»
const DAY = 86400000;

const parseDay = (s) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
const today0 = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const fmt = (d) => (d ? d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');
const plural = (n, f) => {
  const a = Math.abs(n) % 100, b = a % 10;
  if (a > 10 && a < 20) return f[2];
  if (b > 1 && b < 5) return f[1];
  if (b === 1) return f[0];
  return f[2];
};
const daysLeft = (s) => { const d = parseDay(s); return d ? Math.round((d - today0()) / DAY) : null; };
const statusOf = (s) => {
  const left = daysLeft(s);
  if (left == null || left > SOON_DAYS) return 'active';
  return left < 0 ? 'expired' : 'soon';
};
const leftText = (s) => {
  const left = daysLeft(s);
  if (left == null) return 'срок не задан';
  if (left < 0) return `истёк ${-left} ${plural(-left, ['день', 'дня', 'дней'])} назад`;
  if (left === 0) return 'истекает сегодня';
  return `осталось ${left} ${plural(left, ['день', 'дня', 'дней'])}`;
};
const STATUS_LABEL = { active: 'Активен', soon: 'Истекает', expired: 'Истёк' };
const students3 = (n) => `${n} ${plural(n, ['ученик', 'ученика', 'учеников'])}`;

const PRESETS = [
  { key: '1m', months: 1, big: '+1', small: 'месяц' },
  { key: '3w', weeks: 3, big: '+3', small: 'недели' },
  { key: '2w', weeks: 2, big: '+2', small: 'недели' },
  { key: '1w', weeks: 1, big: '+1', small: 'неделя' },
];
const presetOf = (key) => PRESETS.find(p => p.key === key) || PRESETS[0];
// Новая дата — от текущей, а если доступ уже истёк — от сегодня (прошлое не дарим)
const extendFrom = (accessUntil, { months, weeks }) => {
  const cur = parseDay(accessUntil);
  const t = today0();
  const d = new Date(cur && cur > t ? cur : t);
  if (weeks) d.setDate(d.getDate() + weeks * 7);
  else d.setMonth(d.getMonth() + months);
  return d;
};
// Срок в «Истории продлений»; +1 год и +3/+6 мес — от старых пресетов
const deltaLabel = (l) => {
  if (l.weeks) return `+${l.weeks} нед`;
  if (l.months) return l.months === 12 ? '+1 год' : `+${l.months} мес`;
  return 'своя дата';
};
const courseIdsOf = (s) => (s.StudentCourses || []).map(sc => sc.courseId ?? sc.Course?.id).filter(Boolean);

/* ── Иконки ── */
const IcSearch = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>);
const IcCourses = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5M2 12l10 5 10-5" /></svg>);
const IcAllStudents = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>);
const IcX = ({ s = 18 }) => (<svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>);
const IcPlus = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>);
const IcChevron = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>);

const Check = ({ on }) => (
  <span className={`acc-check${on ? ' on' : ''}`}>
    {on && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
  </span>
);
const Ava = ({ s, size = '' }) => (
  <span className={`acc-ava ${size}`} style={s.avatar ? undefined : { background: avatarColor(s.id) }}>
    {s.avatar ? <img src={s.avatar} alt="" /> : <AvatarIcon id={s.id} size={size === 'lg' ? 29 : size === 'sm' ? 20 : 24} />}
  </span>
);

export default function AdminAccessPage() {
  const { can } = useAuth();
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [log, setLog] = useState([]);
  const [courses, setCourses] = useState([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [pickedCourse, setPickedCourse] = useState(null);   // id курса | 'none' | null — все ученики
  const [coursePick, setCoursePick] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [cardId, setCardId] = useState(null);
  const [extendIds, setExtendIds] = useState(null);         // кому продлеваем; окно открыто, пока не null
  const [preset, setPreset] = useState('1m');
  const [customDate, setCustomDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getAccess(), getCourses()])
      .then(([acc, crs]) => {
        setStudents(acc.data?.students || []);
        setLog(acc.data?.log || []);
        setCourses(crs.data || []);
      })
      .catch(() => toast.error('Не удалось загрузить доступы'))
      .finally(() => setLoading(false));
  }, []);

  const courseById = useMemo(() => {
    const m = {};
    courses.forEach(c => { m[c.id] = c; });
    return m;
  }, [courses]);
  const coursesOf = (s) => courseIdsOf(s)
    .map(id => courseById[id] || (s.StudentCourses || []).find(sc => sc.courseId === id)?.Course)
    .filter(Boolean);

  if (loading) return <Loader />;

  // Курс и поиск сужают список; вкладки статуса считают уже внутри него
  const q = query.trim().toLowerCase();
  const base = students.filter(s => {
    const ids = courseIdsOf(s);
    const inCourse = pickedCourse == null || (pickedCourse === 'none' ? ids.length === 0 : ids.includes(pickedCourse));
    const inQuery = !q || (s.name || '').toLowerCase().includes(q) || (s.email || '').toLowerCase().includes(q);
    return inCourse && inQuery;
  });
  const countOf = (st) => base.filter(s => statusOf(s.accessUntil) === st).length;
  const list = base
    .filter(s => status === 'all' || statusOf(s.accessUntil) === status)
    // Пробные — всегда сверху: у них вместо даты пометка
    .sort((a, b) => (b.isTrial ? 1 : 0) - (a.isTrial ? 1 : 0)
      || (parseDay(a.accessUntil) || 0) - (parseDay(b.accessUntil) || 0));

  const allOn = list.length > 0 && list.every(s => selected.has(s.id));
  const toggleOne = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(list.map(s => s.id)));

  const openExtend = (ids) => { setExtendIds(ids); setPreset('1m'); setCustomDate(''); };
  const targets = extendIds ? students.filter(s => extendIds.includes(s.id)) : [];
  const period = presetOf(preset);
  const newDate = customDate ? parseDay(customDate) : (targets.length === 1 ? extendFrom(targets[0].accessUntil, period) : null);

  const confirmExtend = async () => {
    if (!targets.length || saving) return;
    setSaving(true);
    try {
      const res = await extendAccess(targets.map(s => s.id),
        customDate ? { date: customDate } : { months: period.months, weeks: period.weeks });
      const upd = {};
      (res.data?.updated || []).forEach(u => { upd[u.id] = u.accessUntil; });
      setStudents(prev => prev.map(s => (upd[s.id] ? { ...s, accessUntil: upd[s.id] } : s)));
      if (res.data?.log) setLog(res.data.log);
      toast.success(targets.length === 1
        ? `Доступ продлён до ${fmt(parseDay(upd[targets[0].id]))}`
        : `Доступ продлён: ${students3(targets.length)}`);
      setExtendIds(null);
      setSelected(new Set());
    } catch {
      toast.error('Не удалось продлить доступ');
    } finally {
      setSaving(false);
    }
  };

  const cardStudent = students.find(s => s.id === cardId) || null;
  const pickedName = pickedCourse === 'none' ? 'Без курса'
    : pickedCourse != null ? (courseById[pickedCourse]?.name || 'Курс') : 'Выбрать курс';
  const statusOptions = [
    ['all', 'Все', base.length], ['soon', 'Истекают', countOf('soon')],
    ['expired', 'Истекли', countOf('expired')], ['active', 'Активны', countOf('active')],
  ].map(([key, label, n]) => ({ key, label: <span className="acc-tab">{label}<i>{n}</i></span> }));
  const noCourseCount = students.filter(s => courseIdsOf(s).length === 0).length;

  return (
    <div>
      <div className="acc-top">
        <h1 className="s-page-title">Доступы</h1>
        <p className="s-page-sub">Сроки доступа учеников к платформе и продление</p>
      </div>

      {/* ── Поиск · курс · статус ── */}
      <div className="acc-tools">
        <div className="acc-search">
          <IcSearch />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Имя или email..." />
        </div>
        <button type="button" className="stu-pick" onClick={() => setCoursePick(true)} title={pickedCourse != null ? pickedName : undefined}>
          {/* Иконка выбранного курса — она же на его обложке; пока курс не
              выбран, показываем общий значок «курсы» */}
          {pickedCourse != null && pickedCourse !== 'none' && courseById[pickedCourse]?.icon
            ? <LibIcon icon={courseById[pickedCourse].icon} size={16} />
            : <IcCourses />}
          <span className="stu-pick-t">{pickedName}</span>
          {pickedCourse != null && (
            <span className="stu-pick-x" title="Показать всех" onClick={(e) => { e.stopPropagation(); setPickedCourse(null); }}>
              <IcX s={13} />
            </span>
          )}
        </button>
        <SegToggle className="glass" value={status} onChange={setStatus} options={statusOptions} />
      </div>

      {/* ── Массовое продление: появляется, когда выбраны ученики ── */}
      <div className="stu-bulk-wrap" data-open={selected.size > 0}>
        <div className="stu-bulk-inner">
          <div className="stu-bulk">
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--primary-dark)' }}>Выбрано: {selected.size}</span>
            <div style={{ flex: 1 }} />
            {can('access.extend') && (
              <button className="action-btn purple" style={{ padding: '8px 14px', fontSize: 13 }} onClick={() => openExtend([...selected])}>Продлить доступ</button>
            )}
            <button type="button" className="acc-link" onClick={() => setSelected(new Set())}>Снять</button>
          </div>
        </div>
      </div>

      <div className="acc-cols">
        {/* Ученики — в одной стеклянной карточке, сами строки тоже стекло */}
        <section className="acc-panel acc-list">
          <div className="acc-thead">
            <span className="acc-c-check" onClick={toggleAll} title="Выбрать всех"><Check on={allOn} /></span>
            <span>Ученик</span>
            <span>Доступен до</span>
            <span className="acc-c-status">Статус</span>
            <span className="acc-c-chev" />
          </div>
          {list.map(s => {
            const st = statusOf(s.accessUntil);
            const sel = selected.has(s.id);
            return (
              <div key={s.id} className={`acc-row${sel ? ' sel' : ''}`} onClick={() => setCardId(s.id)} title="Открыть карточку ученика">
                <span className="acc-c-check" onClick={(e) => { e.stopPropagation(); toggleOne(s.id); }} title="Выбрать">
                  <Check on={sel} />
                </span>
                <div className="acc-who">
                  <Ava s={s} />
                  <div style={{ minWidth: 0 }}>
                    <div className="acc-name">{s.name || 'Без имени'}</div>
                    <div className="acc-mail">{s.email}</div>
                  </div>
                </div>
                <div className="acc-c-until">
                  {s.isTrial ? (
                    <span className="stu-code stu-code-trial">Пробный</span>
                  ) : (
                    <>
                      <div className="acc-until-d">{fmt(parseDay(s.accessUntil))}</div>
                      <div className="acc-until-l">{leftText(s.accessUntil)}</div>
                    </>
                  )}
                </div>
                <div className="acc-c-status"><span className={`acc-chip ${st}`}>{STATUS_LABEL[st]}</span></div>
                <span className="acc-c-chev"><IcChevron /></span>
              </div>
            );
          })}
          {list.length === 0 && <div className="acc-empty">{students.length ? 'Ничего не найдено' : 'Учеников пока нет'}</div>}
        </section>

        {/* История продлений — справа от учеников */}
        <section className="acc-panel acc-log">
          <div className="panel-cap">История продлений</div>
          {log.length === 0 && <div className="acc-empty sm">Продлений пока не было</div>}
          {log.map(l => (
            <div key={l.id} className="acc-log-row">
              <Ava s={l.student} size="sm" />
              <div className="acc-log-main">
                <div className="acc-name">{l.student.name || l.student.email}</div>
                <div className="acc-mail">{l.student.email}</div>
              </div>
              <div className="acc-log-right">
                <div className="acc-log-delta">{deltaLabel(l)}</div>
                <span className="acc-chip until">до {fmt(parseDay(l.to))}</span>
              </div>
            </div>
          ))}
        </section>
      </div>

      {/* ── Выбор курса — как в «Учениках» ── */}
      <Modal isOpen={coursePick} onClose={() => setCoursePick(false)} bare>
        <div className="adm-modal stu-cm" onClick={e => e.stopPropagation()}>
          <div className="stu-cm-head">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="stu-cm-t">Выбрать курс</div>
              <div className="stu-cm-s">покажем сроки доступа учеников этого курса</div>
            </div>
            <button type="button" className="adm-x" onClick={() => setCoursePick(false)} aria-label="Закрыть"><IcX /></button>
          </div>
          <div className="stu-cm-grid">
            <button type="button" className={`stu-cc plain${pickedCourse == null ? ' on' : ''}`}
              onClick={() => { setPickedCourse(null); setCoursePick(false); }}>
              <span className="stu-cc-plain">
                <span className="stu-cc-plain-ic"><IcAllStudents /></span>
                <span className="stu-cc-plain-t">Все ученики</span>
              </span>
              <span className="stu-cc-foot">{students3(students.length)}</span>
            </button>
            {/* Сначала опубликованные курсы, потом черновики; «Без курса» — в самом конце */}
            {[...courses].sort((a, b) => (a.isActive === false) - (b.isActive === false)).map(c => {
              const cnt = students.filter(s => courseIdsOf(s).includes(c.id)).length;
              const col = c.color || '#7c4dff';
              return (
                <button key={c.id} type="button" className={`stu-cc${pickedCourse === c.id ? ' on' : ''}`}
                  onClick={() => { setPickedCourse(c.id); setCoursePick(false); }}>
                  <span className="stu-cc-cover" style={{
                    '--st-t-light': `${col}80`, '--st-t-dark': `${col}38`,
                    '--st-chip-fg': shade(col, -42), '--st-chip-fg-dark': shade(col, 55), '--st-c': col,
                  }}>
                    <span className="st-crs-pat"><CoverPattern id={`ac${c.id}`} /></span>
                    <span className="stu-cc-top">
                      {c.examType && <span className="st-crs-lvl"><i />{c.examType}</span>}
                      <span className="st-crs-ic"><LibIcon icon={c.icon} size={15} /></span>
                    </span>
                    <span className="stu-cc-name">{c.name}</span>
                  </span>
                  <span className="stu-cc-foot">
                    {students3(cnt)}
                    {c.isActive === false && <i className="stu-cc-draft">черновик</i>}
                  </span>
                </button>
              );
            })}
            <button type="button" className={`stu-cc plain${pickedCourse === 'none' ? ' on' : ''}`}
              onClick={() => { setPickedCourse('none'); setCoursePick(false); }}>
              <span className="stu-cc-plain"><span className="stu-cc-plain-t">Без курса</span></span>
              <span className="stu-cc-foot">{students3(noCourseCount)}</span>
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Карточка ученика: дата доступа, курсы, продление ── */}
      <Modal isOpen={!!cardStudent} onClose={() => setCardId(null)} bare>
        {cardStudent && (() => {
          const st = statusOf(cardStudent.accessUntil);
          const until = fmt(parseDay(cardStudent.accessUntil));
          const cs = coursesOf(cardStudent);
          return (
            <div className="adm-modal acc-card" onClick={e => e.stopPropagation()}>
              <div className="acc-card-head">
                <Ava s={cardStudent} size="lg" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="acc-card-name">{cardStudent.name || 'Без имени'}</div>
                  <div className="acc-card-sub">
                    <span>{cardStudent.email}</span>
                    {cardStudent.isTrial
                      ? <span className="stu-code stu-code-trial sm">Пробный</span>
                      : <span className={`acc-chip lg ${st}`}>{st === 'expired' ? `Доступ истёк ${until}` : `Доступ до ${until}`}</span>}
                  </div>
                </div>
                <button type="button" className="adm-x" onClick={() => setCardId(null)} aria-label="Закрыть"><IcX /></button>
              </div>
              <div className="acc-card-body">
                <div className="acc-until">
                  <div className="acc-until-top">
                    <span className="acc-cap">Доступен до</span>
                    {can('access.extend') && (
                      <button type="button" className="action-btn purple acc-extend" onClick={() => openExtend([cardStudent.id])}>
                        <IcPlus />Продлить
                      </button>
                    )}
                  </div>
                  <div className="acc-until-big">
                    {cardStudent.isTrial ? (
                      <span className="acc-trial-big">Пробный — дата появится, когда продлите доступ</span>
                    ) : (
                      <>
                        <span className="acc-bebas">{until}</span>
                        <span className={`acc-left ${st}`}>{leftText(cardStudent.accessUntil)}</span>
                      </>
                    )}
                  </div>
                  <div className="acc-courses">
                    {cs.length === 0 && <div className="acc-mail">Ученик пока не записан ни на один курс</div>}
                    {cs.map(c => (
                      <div key={c.id} className="acc-course">
                        <span className="acc-course-ic"><LibIcon icon={c.icon} size={16} /></span>
                        <span className="acc-course-name">{c.name}</span>
                        <span className={`acc-chip ${st}`}>{STATUS_LABEL[st]}</span>
                      </div>
                    ))}
                  </div>
                  <div className="acc-hint">
                    Доступ один на ученика и действует на все его курсы. Эту же дату ученик видит в профиле — «Доступ до».
                    По истечении ничего не блокируется: это подсказка для вас и ученика.
                  </div>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* ── Продление: пресеты, своя дата, итог ── */}
      <Modal isOpen={!!extendIds} onClose={() => { if (!saving) setExtendIds(null); }} bare>
        <div className="adm-modal acc-ext" onClick={e => e.stopPropagation()}>
          <div className="stu-cm-head">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="stu-cm-t">Продлить доступ</div>
              <div className="stu-cm-s">
                {targets.length === 1
                  ? (targets[0].name || targets[0].email)
                  : `${students3(targets.length)} · продлим каждому от его текущей даты`}
              </div>
            </div>
            <button type="button" className="adm-x" onClick={() => setExtendIds(null)} aria-label="Закрыть"><IcX /></button>
          </div>
          <div className="acc-ext-body">
            <div className="acc-cap">На сколько продлить</div>
            <div className="acc-presets">
              {PRESETS.map(p => (
                <button key={p.key} type="button" className={`acc-preset${!customDate && preset === p.key ? ' on' : ''}`}
                  onClick={() => { setPreset(p.key); setCustomDate(''); }}>
                  <span className="acc-preset-big">{p.big}</span>
                  <span className="acc-preset-small">{p.small}</span>
                </button>
              ))}
            </div>
            <div className="acc-cap acc-ext-date">Или своя дата</div>
            <DatePicker value={customDate} onChange={setCustomDate} placeholder="Выберите дату" />
            <div className="acc-newdate">
              <span>Новая дата доступа</span>
              {newDate
                ? <span className="acc-bebas">{fmt(newDate)}</span>
                : <span className="acc-newdate-each">у каждого своя</span>}
            </div>
          </div>
          <div className="acc-ext-foot">
            <button type="button" className="action-btn secondary" onClick={() => setExtendIds(null)} disabled={saving}>Отмена</button>
            <button type="button" className="action-btn purple" onClick={confirmExtend} disabled={saving || !targets.length}>
              {saving ? 'Сохраняем…' : targets.length > 1 ? `Продлить ${targets.length}` : 'Продлить'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
